import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronDown, Gift, Lock, ScrollText, User, Clock } from 'lucide-react';
import { getBackendUrl } from '../../services/origin';
import { DEFAULT_BRANDING, PIN_STORE_PREFIX, TIER_COLORS } from './constants';

const API = getBackendUrl();

/**
 * Single-reward detail page (McDonald's-style hero + body).
 *
 * Mounted at `/my/:token/premio/:rewardId`. Shows:
 *  • Big square hero (image_url with brand-colored fallback when missing)
 *  • Title + points cost in the corner
 *  • Description
 *  • "Requisiti" list — derived from the reward's tier/time/days/daily-limit
 *    constraints
 *  • "Come funziona" expandable
 *  • Optional reward-specific T&C section (admin-set)
 *  • "Apri T&C generali" link to the brand T&C page
 *  • CTA "Riscatta in cassa" (visible info, no payment flow yet)
 *
 * Reuses the cached PIN from `/my/:token` to authenticate the dashboard
 * data fetch, so the user lands here straight from a card click without
 * being asked to re-enter the PIN.
 */
export default function RewardDetailPage() {
  const { token, rewardId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [howToOpen, setHowToOpen] = useState(false);

  const cachedPin = useMemo(() => {
    try { return localStorage.getItem(PIN_STORE_PREFIX + token) || ''; } catch { return ''; }
  }, [token]);

  useEffect(() => {
    if (!cachedPin) {
      // No cached PIN → bounce to the dashboard which will ask for PIN.
      navigate(`/my/${token}`, { replace: true });
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/my/${token}/data`, { params: { pin: cachedPin } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          // PIN no longer valid — strip cache, redirect to PIN entry.
          try { localStorage.removeItem(PIN_STORE_PREFIX + token); } catch {}
          navigate(`/my/${token}`, { replace: true });
        } else {
          setError(err.response?.data?.detail || 'Impossibile caricare il premio');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, cachedPin, navigate]);

  const branding = useMemo(() => ({ ...DEFAULT_BRANDING, ...(data?.branding || {}) }), [data]);
  const reward = useMemo(() => (data?.rewards || []).find(r => r.id === rewardId), [data, rewardId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: branding.background_color }} data-testid="reward-detail-loading">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: `${branding.primary_color}30`, borderTopColor: branding.primary_color }} />
      </div>
    );
  }

  if (error || !reward) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: branding.background_color }} data-testid="reward-detail-error">
        <Gift size={42} style={{ color: branding.text_secondary_color }} className="mb-3" />
        <p className="text-base font-bold" style={{ color: branding.text_color }}>
          {error || 'Premio non trovato'}
        </p>
        <button onClick={() => navigate(`/my/${token}?tab=premi`)}
          className="mt-4 px-5 py-2 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: branding.primary_color }}>
          Torna ai premi
        </button>
      </div>
    );
  }

  const pc = branding.primary_color;
  const sc = branding.secondary_color;
  const tc = branding.text_color;
  const ts = branding.text_secondary_color;
  const heroBg = pc;

  // Build "Requisiti" rows from the reward's metadata. Each row turns
  // into a check-or-cross icon depending on whether the customer
  // currently meets it.
  const reqs = [];
  // Tier requirement
  if (reward.min_tier && reward.min_tier !== 'bronze') {
    const tierColor = TIER_COLORS[reward.min_tier] || pc;
    reqs.push({
      icon: <span className="capitalize text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${tierColor}20`, color: tierColor }}>{reward.min_tier}</span>,
      label: `Tier minimo: ${data.tier_names?.[reward.min_tier] || reward.min_tier}`,
      met: !!reward.can_redeem,
    });
  }
  // Hour window
  if (reward.available_from_hour != null && reward.available_to_hour != null) {
    reqs.push({
      icon: <Clock size={14} style={{ color: pc }} />,
      label: `Disponibile tra le ${reward.available_from_hour}:00 e le ${reward.available_to_hour}:00`,
      met: !!reward.available_today,
    });
  }
  // Day-of-week
  if (reward.allowed_days && reward.allowed_days.length > 0 && reward.allowed_days.length < 7) {
    const dayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
    reqs.push({
      icon: <Clock size={14} style={{ color: pc }} />,
      label: `Solo: ${reward.allowed_days.map(d => dayLabels[d]).join(', ')}`,
      met: !!reward.available_today,
    });
  }
  // Always: must be registered (the screenshot reference shows this)
  reqs.push({
    icon: <User size={14} style={{ color: pc }} />,
    label: 'Utenti registrati al programma loyalty',
    met: true,
  });

  const pointsProgress = Math.min(100, Math.round((data.points / reward.points_cost) * 100));
  const pointsRemaining = Math.max(0, reward.points_cost - data.points);
  const canRedeem = !!reward.can_redeem && !!reward.available_today;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: branding.background_color }} data-testid="reward-detail-page">
      {/* Hero — square aspect ratio, McDonald's style */}
      <div className="relative w-full" style={{ backgroundColor: heroBg, aspectRatio: '1 / 1', maxHeight: '60vh' }} data-testid="reward-detail-hero">
        {/* Back button */}
        <button onClick={() => navigate(`/my/${token}?tab=premi`)}
          data-testid="reward-detail-back"
          className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center active:scale-95 transition-transform">
          <ArrowLeft size={18} className="text-[#1A1A1A]" />
        </button>

        {/* Image / fallback */}
        {reward.image_url ? (
          <img src={reward.image_url} alt={reward.name}
            className="w-full h-full object-contain"
            data-testid="reward-detail-image" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[120px]" data-testid="reward-detail-emoji">
            {reward.emoji || '🎁'}
          </div>
        )}

        {/* Points cost badge — bottom right corner like the McDonald's "€1" */}
        <div className="absolute bottom-4 right-5 text-right pointer-events-none" data-testid="reward-detail-cost">
          <p className="text-5xl sm:text-6xl font-black tracking-tight drop-shadow-md leading-none"
            style={{ color: '#FFFFFF' }}>
            {reward.points_cost}
          </p>
          <p className="text-xs font-bold tracking-widest uppercase mt-1" style={{ color: '#FFFFFFcc' }}>punti</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-lg mx-auto px-5 pt-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: tc }} data-testid="reward-detail-title">
          {reward.name}
        </h1>
        {reward.description && (
          <p className="text-sm leading-relaxed mb-5" style={{ color: ts }} data-testid="reward-detail-description">
            {reward.description}
          </p>
        )}

        {/* Points progress for not-yet-redeemable rewards */}
        {!canRedeem && pointsRemaining > 0 && (
          <div className="rounded-2xl p-4 mb-5 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}0a` }} data-testid="reward-detail-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs" style={{ color: ts }}>I tuoi punti</span>
              <span className="text-xs font-bold tabular-nums" style={{ color: pc }}>{data.points} / {reward.points_cost}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pointsProgress}%`, backgroundColor: pc }} />
            </div>
            <p className="text-xs mt-2 font-medium" style={{ color: pc }}>
              Mancano {pointsRemaining} punti per riscattare
            </p>
          </div>
        )}

        {/* Requisiti */}
        <h2 className="text-base font-bold mb-3" style={{ color: tc }} data-testid="reward-detail-requirements-title">Requisiti</h2>
        <div className="rounded-2xl border overflow-hidden mb-5" style={{ backgroundColor: `${tc}04`, borderColor: `${tc}0a` }} data-testid="reward-detail-requirements">
          {reqs.map((req, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i < reqs.length - 1 ? 'border-b' : ''}`} style={{ borderColor: `${tc}08` }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: req.met ? `${sc}15` : `${pc}10` }}>
                {req.icon}
              </div>
              <p className="flex-1 text-sm" style={{ color: tc }}>{req.label}</p>
              {req.met ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={sc} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <Lock size={14} style={{ color: ts }} />
              )}
            </div>
          ))}
        </div>

        {/* Reward-specific T&C from admin */}
        {reward.terms && (
          <>
            <h2 className="text-base font-bold mb-3" style={{ color: tc }}>Termini & Condizioni</h2>
            <div className="rounded-2xl p-4 border mb-5 whitespace-pre-line text-sm leading-relaxed"
              style={{ backgroundColor: `${tc}04`, borderColor: `${tc}0a`, color: ts }}
              data-testid="reward-detail-terms">
              {reward.terms}
            </div>
          </>
        )}

        {/* Brand-level T&C link */}
        <a href="/event/termini-e-condizioni" target="_blank" rel="noopener noreferrer"
          data-testid="reward-detail-brand-tc-link"
          className="flex items-center gap-2 text-sm font-semibold mb-5" style={{ color: '#3b82f6' }}>
          <ScrollText size={14} />
          Termini & Condizioni generali
        </a>

        {/* "Come funziona" expandable */}
        <button onClick={() => setHowToOpen(o => !o)}
          data-testid="reward-detail-how-to-toggle"
          className="w-full rounded-2xl border p-4 flex items-center gap-3 transition-colors active:opacity-80"
          style={{ borderColor: `${tc}10`, backgroundColor: `${tc}04` }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}15` }}>
            <span className="text-base font-bold" style={{ color: pc }}>?</span>
          </div>
          <p className="flex-1 text-left text-sm font-bold" style={{ color: tc }}>Come funziona</p>
          <ChevronDown size={16} style={{ color: ts }} className={`transition-transform ${howToOpen ? 'rotate-180' : ''}`} />
        </button>
        {howToOpen && (
          <div className="mt-2 rounded-2xl p-4 border text-sm leading-relaxed space-y-2"
            style={{ backgroundColor: `${tc}03`, borderColor: `${tc}0a`, color: ts }}
            data-testid="reward-detail-how-to-content">
            <p>1. Accumula punti con ogni acquisto presso {data.business_name}.</p>
            <p>2. Quando raggiungi i <strong style={{ color: tc }}>{reward.points_cost} punti</strong>, il premio diventa riscattabile.</p>
            <p>3. Mostra in cassa il QR code della tua bacheca: lo staff confermera il riscatto e i punti verranno scalati dal tuo saldo.</p>
            {reward.daily_limit && <p>4. Massimo {reward.daily_limit} riscatti al giorno per cliente.</p>}
          </div>
        )}
      </div>

      {/* Sticky CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t backdrop-blur-xl z-40"
        style={{ backgroundColor: `${branding.background_color}ee`, borderColor: `${tc}10` }}
        data-testid="reward-detail-cta-bar">
        <div className="max-w-lg mx-auto px-5 py-3">
          <button
            onClick={() => navigate(`/my/${token}?tab=premi`)}
            disabled={!canRedeem}
            data-testid="reward-detail-cta-btn"
            className="w-full h-12 rounded-2xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: canRedeem ? pc : `${tc}10`,
              color: canRedeem ? '#FFFFFF' : ts,
            }}>
            {canRedeem ? 'Mostra in cassa per riscattare' : `Mancano ${pointsRemaining} punti`}
          </button>
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" style={{ backgroundColor: `${branding.background_color}ee` }} />
      </div>
    </div>
  );
}
